import { Row } from '@tanstack/react-table';
import { Link } from 'react-router';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx';
import { Button } from '@/components/ui/button.tsx';
import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import { useSession } from '@/services/providers/use-session.ts';
import { usePermissions } from '@/hooks/use-permission.ts';
import { useDeletePlantQuery } from '@/features/plants/hooks/mutations/[plantId]/use-delete-plant.ts';
import { useQueryClient } from '@tanstack/react-query';
import { PlantUserAccessManagementDialog } from '@/features/plants/components/plant-management/user-access/plant-user-access-management.tsx';
import { PlantsWithOverview } from '@/features/plants/hooks/queries/use-get-plants.tsx';

export function PlantsTableRowActions({ row }: { row: Row<PlantsWithOverview> }) {
  const plant = row.original;
  const queryClient = useQueryClient();
  const deletePlantMutation = useDeletePlantQuery({ queryClient });

  const [
    canViewPlant,
    canViewControlPanel,
    canViewEdit,
    canViewConnectionDetails,
    canViewAccessManagement,
    canDelete,
  ] = usePermissions<'plants'>([
    ['plants', 'view-page.plant'],
    ['plants', 'view-page.control-panel'],
    ['plants', 'view-page.edit'],
    ['plants', 'view-page.connection-details'],
    ['plants', 'view-page.user-access-management'],
    ['plants', 'delete'],
  ]);

  const allowedUsers = ['catalin.petrovici@nokto.eu', 'alex.paval@nokto.eu'];
  const { email } = useSession();

  return (
    <div className="flex justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="data-[state=open]:bg-muted flex h-8 w-8 p-0 hover:bg-gray-200"
          >
            <DotsHorizontalIcon className="h-5 w-5" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {canViewPlant && (
            <Link to={plant.plantId}>
              <DropdownMenuItem>Overview Plant</DropdownMenuItem>
            </Link>
          )}

          {canViewControlPanel && (
            <Link to={`${plant.plantId}/control-panel`}>
              <DropdownMenuItem>Control Panel</DropdownMenuItem>
            </Link>
          )}

          {canViewControlPanel && (
            <Link to={`${plant.plantId}/schedule`}>
              <DropdownMenuItem>Schedule</DropdownMenuItem>
            </Link>
          )}

          {canViewEdit && (
            <Link to={`/edit-plant/${plant.plantId}`}>
              <DropdownMenuItem>Edit Plant</DropdownMenuItem>
            </Link>
          )}

          {canViewConnectionDetails && (
            <Link to={`${plant.plantId}/connection-details`}>
              <DropdownMenuItem>View Connection Details</DropdownMenuItem>
            </Link>
          )}

          {canViewAccessManagement && (
            <PlantUserAccessManagementDialog plant={plant}>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                }}
              >
                User Access Management
              </DropdownMenuItem>
            </PlantUserAccessManagementDialog>
          )}

          {canDelete && (
            <DropdownMenuItem disabled={!allowedUsers.includes(email ?? '')}>
              <button
                className="flex h-full w-full"
                onClick={() => {
                  deletePlantMutation.mutate({
                    plantId: plant.plantId,
                  });
                }}
              >
                Delete plant
              </button>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
